import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { feeCategoryAPI } from '../utils/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import '../styles/list.css';
import '../styles/admins.css';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const initialForm = {
  name: '',
  isEnabled: true,
  isDefault: false,
};

const FeeCategories = () => {
  const [feeCategories, setFeeCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedFeeCategory, setSelectedFeeCategory] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const normalizedSearch = debouncedSearch.trim().toLowerCase();
  const isSearchReady = !normalizedSearch || normalizedSearch.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    fetchFeeCategories();
  }, []);

  const fetchFeeCategories = async () => {
    setLoading(true);
    try {
      const response = await feeCategoryAPI.getAll({ includeDisabled: 1 });
      setFeeCategories(response.data.data.feeCategories || []);
    } catch (fetchError) {
      setError(fetchError.response?.data?.message || 'Failed to load fee categories');
    } finally {
      setLoading(false);
    }
  };

  const filteredFeeCategories = feeCategories.filter((item) => {
    if (!isSearchReady || !normalizedSearch) {
      return true;
    }

    return item.name.toLowerCase().includes(normalizedSearch);
  });

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedFeeCategory(null);
    setFormData(initialForm);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setModalMode('edit');
    setSelectedFeeCategory(item);
    setFormData({
      name: item.name,
      isEnabled: Number(item.isEnabled) === 1,
      isDefault: Number(item.isDefault) === 1,
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setShowModal(false);
    setSelectedFeeCategory(null);
    setFormData(initialForm);
    setError('');
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Fee category name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        isEnabled: formData.isEnabled,
        isDefault: formData.isDefault,
      };

      if (modalMode === 'edit' && selectedFeeCategory) {
        await feeCategoryAPI.update(selectedFeeCategory.id, payload);
        setSuccess('Fee category updated successfully');
      } else {
        await feeCategoryAPI.create(payload);
        setSuccess('Fee category created successfully');
      }

      closeModal(true);
      fetchFeeCategories();
    } catch (submitError) {
      setError(submitError.response?.data?.message || 'Failed to save fee category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="list-page">
        <div className="list-header">
          <div>
            <h1>Manage Fee Categories</h1>
            <p className="admin-page-subtitle">Super admins can create, edit, and enable or disable fee categories for fee payment entry.</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <i className="fas fa-plus"></i> Add Fee Category
          </button>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && !showModal && <div className="alert alert-error">{error}</div>}

        <div className="card">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Type at least 3 characters to search by fee category name..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <i className="fas fa-magnifying-glass"></i>
          </div>

          {!isSearchReady && (
            <div className="alert alert-info">Type at least 3 characters to search.</div>
          )}

          {loading ? (
            <div className="spinner"></div>
          ) : filteredFeeCategories.length > 0 ? (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fee Category</th>
                    <th>Status</th>
                    <th>Default</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeeCategories.map((item) => {
                    const isEnabled = Number(item.isEnabled) === 1;

                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>
                          <span className={`badge ${isEnabled ? 'badge-success' : 'badge-warning'}`}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td>
                          {Number(item.isDefault) === 1 ? (
                            <span className="badge badge-info">Default</span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN') : '-'}</td>
                        <td className='width-100'>
                          <div className="action-buttons">
                            <button className="btn btn-secondary" onClick={() => openEditModal(item)}>
                              <i className="fas fa-pen"></i> Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info">No fee categories found.</div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content admin-modal" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <h2>{modalMode === 'edit' ? 'Edit Fee Category' : 'Create Fee Category'}</h2>
                <button className="modal-close" onClick={closeModal} type="button">
                  x
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && <div className="alert alert-error">{error}</div>}

                  <div className="form-group">
                    <label>Fee Category Name*</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter fee category name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isEnabled"
                        checked={formData.isEnabled}
                        onChange={handleChange}
                        className="me-2"
                      />
                      Enable this fee category for fee payment add and edit
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isDefault"
                        checked={formData.isDefault}
                        onChange={handleChange}
                        className="me-2"
                      />
                      Select this as the default fee category in fee payment form
                    </label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : modalMode === 'edit' ? 'Update Fee Category' : 'Create Fee Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FeeCategories;
